import React, { useState, useMemo } from 'react';
import { DollarSign, TrendingUp, Package, Factory, Truck, Users, CreditCard, Clock, Archive, AlertTriangle, PieChart, RefreshCcw, Lock, ShieldAlert, Warehouse, Presentation } from 'lucide-react';
import SalesReport from './SalesReport';
import PurchaseReport from './PurchaseReport';
import StockReport from './StockReport';
import ProductionReport from './ProductionReport';
import DispatchReport from './DispatchReport';
import VendorReport from './VendorReport';
import DebtorsReport from './DebtorsReport';
import PaymentAgeingReport from './PaymentAgeingReport';
import StockAgeingReport from './StockAgeingReport';
import StockReorderAlerts from './StockReorderAlerts';
import StockSanityReport from './StockSanityReport';
import StockRegisters from './StockRegisters';
import MarginsPricingReport from './MarginsPricingReport';
import InvoiceReconciliationReport from './InvoiceReconciliationReport';
import AmendedOCsReport from './AmendedOCsReport';
import MonthlyReviewCenter from './MonthlyReviewCenter';
import { usePermissions } from '../../hooks/usePermissions';

const tabs = [
  { id: 'board-pack', label: 'Monthly Review Center', icon: Presentation, perm: 'reports.board_pack' },
  { id: 'sales', label: 'Sales', icon: DollarSign, perm: 'reports.sales' },
  { id: 'margins', label: 'Margins & Pricing', icon: PieChart, perm: 'reports.margins' },
  { id: 'invoice-recon', label: 'Invoice Reconciliation', icon: CreditCard, perm: 'reports.invoice_recon' },
  { id: 'amended', label: 'Amended OCs', icon: RefreshCcw, perm: 'reports.amended' },
  { id: 'purchase', label: 'Purchase', icon: TrendingUp, perm: 'reports.purchase' },
  { id: 'stock', label: 'Stock', icon: Package, perm: 'reports.stock' },
  { id: 'stock-registers', label: 'Stock Registers', icon: Warehouse, perm: 'reports.stock' },
  { id: 'production', label: 'Production', icon: Factory, perm: 'reports.production' },
  { id: 'dispatch', label: 'Dispatch', icon: Truck, perm: 'reports.dispatch' },
  { id: 'vendor', label: 'Vendor Analytics', icon: Users, perm: 'reports.vendor' },
  { id: 'debtors', label: 'Debtors', icon: CreditCard, perm: 'reports.debtors' },
  { id: 'ageing', label: 'Payment Ageing', icon: Clock, perm: 'reports.payment_ageing' },
  { id: 'stock-ageing', label: 'Stock Ageing', icon: Archive, perm: 'reports.stock_ageing' },
  { id: 'reorder', label: 'Reorder Alerts', icon: AlertTriangle, perm: 'reports.reorder' },
  { id: 'stock-sanity', label: 'Stock Sanity', icon: ShieldAlert, perm: 'reports.stock_sanity' },
];

const ReportsModule = () => {
  const { hasPermission, loading: permsLoading } = usePermissions();
  const visibleTabs = useMemo(() => tabs.filter(t => hasPermission(t.perm)), [hasPermission, permsLoading]); // eslint-disable-line
  const [activeTab, setActiveTab] = useState(null);

  const currentTab = activeTab && visibleTabs.some(t => t.id === activeTab)
    ? activeTab
    : (visibleTabs[0]?.id || null);

  return (
    <div className="p-5 lg:p-6" data-testid="reports-module">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Reports & Analytics</h1>
        <p className="text-xs text-gray-500 mt-0.5">Comprehensive reports across all departments — powered by live ERP data</p>
      </div>

      {!permsLoading && visibleTabs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="reports-no-access">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <Lock className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-bold text-gray-800">No Report Access</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-md">
            Your role does not have access to any report. Please contact your administrator
            to enable reports under <strong>Settings → Role Permissions → Reports</strong>.
          </p>
        </div>
      )}

      {visibleTabs.length > 0 && (
        <>
          <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit overflow-x-auto">
            {visibleTabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`report-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all whitespace-nowrap ${
                  currentTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>

          <div className="mt-5">
            {currentTab === 'board-pack' && <MonthlyReviewCenter />}
            {currentTab === 'sales' && <SalesReport />}
            {currentTab === 'margins' && <MarginsPricingReport />}
            {currentTab === 'invoice-recon' && <InvoiceReconciliationReport />}
            {currentTab === 'purchase' && <PurchaseReport />}
            {currentTab === 'stock' && <StockReport />}
            {currentTab === 'stock-registers' && <StockRegisters />}
            {currentTab === 'production' && <ProductionReport />}
            {currentTab === 'dispatch' && <DispatchReport />}
            {currentTab === 'vendor' && <VendorReport />}
            {currentTab === 'debtors' && <DebtorsReport />}
            {currentTab === 'ageing' && <PaymentAgeingReport />}
            {currentTab === 'stock-ageing' && <StockAgeingReport />}
            {currentTab === 'reorder' && <StockReorderAlerts />}
            {currentTab === 'stock-sanity' && <StockSanityReport />}
            {currentTab === 'amended' && <AmendedOCsReport />}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsModule;
